import { map } from 'rxjs/operators';
import { DataManagerService } from '../../../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../../../shared/constants/app-constants';
import { TopbarSearchResult } from '../../topbar-search.model';
import { SearchProvider } from '../record-search.model';
import { formatTxDateTime, formatUsd, joinParts, listSearch, maskedCardNumber, REDSHIFT_PARAMS, REDSHIFT_TIMEOUT_MS } from './list-search';

/** Taxi merchants list (and open) their charges from a separate endpoint. */
export const TAXI_CHARGES_SERVICE_NAME = '/taxi/charges';

/**
 * Card transactions — GET /charges, or /taxi/charges for TAXI merchants, exactly as
 * PaymentListComponent / PaymentDetailsComponent choose it; searched in Redshift (src=R).
 * Amounts are in cents. The result shows the amount, the MASKED card number (first6 + last4 only,
 * middle masked - never the middle digits / a full PAN, PCI) and the cardholder name, with the
 * transaction date/time + status below; the card brand is carried separately on `brand` and drawn
 * as a logo (payarc-card-mark), like the Card Transactions list. The opaque charge id is used only
 * to navigate. Opens /payment/payment-details?id=<charge id>, as a row click on the list does.
 */
export function chargesProvider(dataManager: DataManagerService): SearchProvider {
  return {
    group: 'Transactions',
    parent: 'payment',
    accepts: () => true,
    // Resolved per search: the user (and merchant type) changes on login / account switch.
    search: query => listSearch(dataManager, chargesServiceName(dataManager), query,
      chargesParams(dataManager), REDSHIFT_TIMEOUT_MS).pipe(
      map(rows => rows.map(toResult).filter(Boolean))
    )
  };
}

/**
 * search_window=auto (GET /charges only): the backend looks back a window sized to the account's
 * volume - the whole history for most accounts, the last few weeks for the largest ones, whose
 * full-history search outran the timeout. /taxi/charges does not support it, so it is not sent.
 */
export const SEARCH_WINDOW_PARAMS: Record<string, string> = { search_window: 'auto' };

function chargesParams(dataManager: DataManagerService): Record<string, string> {
  return chargesServiceName(dataManager) === TAXI_CHARGES_SERVICE_NAME
    ? REDSHIFT_PARAMS
    : { ...REDSHIFT_PARAMS, ...SEARCH_WINDOW_PARAMS };
}

function chargesServiceName(dataManager: DataManagerService): string {
  return dataManager.user?.['merchant_type'] === 'TAXI' ? TAXI_CHARGES_SERVICE_NAME : AppConstants.SERVICE_NAME_CHARGE;
}

function toResult(row: any): TopbarSearchResult {
  if (!row?.id) {
    return null;
  }
  const amount = formatUsd(Number(row.amount) / 100);
  const cardData = row.card?.data ?? row.card;
  const cardNumber = maskedCardNumber(row.card);
  const cardHolder = cardData?.card_holder_name;
  return {
    group: 'Transactions',
    // the brand is drawn as a logo (payarc-card-mark) from this, like the Card Transactions list
    brand: cardData?.brand || undefined,
    // after the amount: masked card number (first6 + last4) + cardholder name
    label: joinParts(amount, cardNumber, cardHolder) || String(row.id),
    // transaction date/time, then status
    sublabel: joinParts(formatTxDateTime(row.created_at), row.status),
    route: ['/payment/payment-details'],
    queryParams: { id: row.id }
  };
}
